import React, { useContext, useEffect, useCallback, useState } from "react";
import { FieldPath, useForm } from "react-hook-form";
import debounce from "lodash.debounce";
import { SpinningButton } from "Components/ReactstrapSlim";
import { DatePickerInputControlled, CapacityInputControlled } from "Components/Inputs";
import { Module, ModuleCaption } from "Components/Module";
import { WorkspaceReservationMethod, SpaceType, ForceableExtendedSearchOptionsProperties, IBaseSearchRequest, ReservationMethod, CurrentLocationSearchRequest, FullTextSearchRequest, CollectionSearchAutocomplete, SavedSearchRequest, BaseSearchRequest, VenueSearchRequest, VenueGroupSearchRequest, GeoSearchRequest, SavedSearchRequestAutocomplete, VenueSearchAutocomplete, VenueGroupSearchAutocomplete, GeoSearchAutocomplete } from "Services/API/API";
import { SearchAutocompleteWithDropdown } from "../Inputs/SearchAutocompleteWithDropdown";
import { SearchContext } from "../Main/SearchContextProvider";
import SpaceTypeToggles from "../SpaceTypeToggles";
import UserContext from "../../UserContext";
import { FullTextSearchAutocomplete, KnownSearchAutocomplete, KnownSearchRequest } from "Models/Search";
import moment from "moment";
import exhaustiveCheck from "Utils/exhaustiveCheck";
import { contains } from "Models/SpaceType";
import localMoment from "Utils/moment/localMoment";

enum SearchModuleType {
   CurrentLocation,
   FullText,
   Geo,
   Venue,
   VenueGroup,
   SavedSearch
}

function getBaseSearchModuleForm(option: BaseSearchRequest): Partial<SearchModuleForm> {
   return {
      reservationMethod: option.reservationMethod,
      reservationLengthMinutes: option.reservationLengthMinutes,
      spaceTypes: option.spaceTypes,
      minPrice: option.minPrice,
      maxPrice: option.maxPrice,
      minCapacity: option.minCapacity,
      amenityIds: option.amenityIds,
      startTime: option.startTime ? moment.parseZone(option.startTime).toDate() : undefined,
   }
}
function getSearchModuleForm(option: KnownSearchAutocomplete | KnownSearchRequest): Partial<SearchModuleForm> {
   if (option instanceof FullTextSearchAutocomplete) {
      return {
         type: SearchModuleType.FullText,
         query: option.fullTextQuery
      };
   }
   else if (option instanceof GeoSearchAutocomplete) {
      return {
         type: SearchModuleType.Geo,
         query: option.geoQuery
      };
   }
   else if (option instanceof VenueSearchAutocomplete) {
      return {
         type: SearchModuleType.Venue,
         query: option.venueName,
         venueId: option.venueId
      };
   }
   else if (option instanceof VenueGroupSearchAutocomplete) {
      return {
         type: SearchModuleType.VenueGroup,
         query: option.venueGroupName,
         venueGroupId: option.venueGroupId
      };
   }
   else if (option instanceof CollectionSearchAutocomplete) {
      return {
         type: SearchModuleType.FullText,
         collectionId: option.collectionId,
         query: option.collectionName
      };
   }
   else if (option instanceof SavedSearchRequestAutocomplete) {
      return {
         type: SearchModuleType.SavedSearch,
         query: option.predefinedText,
         savedSearchId: option.savedSearchId
      };
   }

   else if (option instanceof GeoSearchRequest) {
      return {
         ...getBaseSearchModuleForm(option),
         type: SearchModuleType.Geo,
         query: option.address
      };
   }
   else if (option instanceof CurrentLocationSearchRequest) {
      return {
         ...getBaseSearchModuleForm(option),
         type: SearchModuleType.CurrentLocation,
         query: undefined
      };
   }
   else if (option instanceof FullTextSearchRequest) {
      return {
         ...getBaseSearchModuleForm(option),
         type: SearchModuleType.FullText,
         query: option.fullTextQuery
      };
   }
   else if (option instanceof VenueSearchRequest) {
      return {
         ...getBaseSearchModuleForm(option),
         type: SearchModuleType.Venue,
         venueId: option.venueId
      };
   }
   else if (option instanceof VenueGroupSearchRequest) {
      return {
         ...getBaseSearchModuleForm(option),
         type: SearchModuleType.VenueGroup,
         venueGroupId: option.venueGroupId
      };
   }
   else {
      exhaustiveCheck(option);
      throw new Error("not implemented")
   }
}

function getPredefinedAddress(savedSearchRequest: SavedSearchRequest): string {
   return savedSearchRequest.metadata.prdefinedText ?? ""
}

function getBaseSearchRequest(searchRequestForm: SearchModuleForm): IBaseSearchRequest {
   return {
      reservationMethod: searchRequestForm.reservationMethod,
      reservationLengthMinutes: searchRequestForm.reservationLengthMinutes,
      spaceTypes: searchRequestForm.spaceTypes,
      minPrice: searchRequestForm.minPrice,
      maxPrice: searchRequestForm.maxPrice,
      minCapacity: searchRequestForm.minCapacity,
      amenityIds: searchRequestForm.amenityIds,
      startTime: searchRequestForm.startTime
         ? localMoment(moment(searchRequestForm.startTime)).toISOString()
         : undefined,
   };
}

function getSearchRequest(searchFormValues: SearchModuleForm): KnownSearchRequest | Guid {
   switch (searchFormValues.type) {
      case SearchModuleType.CurrentLocation:
         return new CurrentLocationSearchRequest();
      case SearchModuleType.FullText: {
         return new FullTextSearchRequest({
            ...getBaseSearchRequest(searchFormValues),
            fullTextQuery: searchFormValues.query
         });
      }
      case SearchModuleType.Geo: {
         return new GeoSearchRequest({
            ...getBaseSearchRequest(searchFormValues),
            address: searchFormValues.query
         });
      }
      case SearchModuleType.Venue: {
         return new VenueSearchRequest({
            ...getBaseSearchRequest(searchFormValues),
            venueId: searchFormValues.venueId!
         });
      }
      case SearchModuleType.VenueGroup: {
         return new VenueGroupSearchRequest({
            ...getBaseSearchRequest(searchFormValues),
            venueGroupId: searchFormValues.venueGroupId!
         });
      }
      case SearchModuleType.SavedSearch:
         return searchFormValues.savedSearchId!;
      default:
         exhaustiveCheck(searchFormValues.type);
         throw new Error(`Not supported search type ${searchFormValues.type}`);
   }
}

const isAutosubmitable = (function initAutosubmitableProperties(properties: FieldPath<SearchModuleForm>[]) {
   return function (name?: FieldPath<SearchModuleForm>) {
      return name != null && properties.indexOf(name) > -1;
   }
})(["startTime", "minCapacity", "spaceTypes"]);

export type SearchModuleForm = Omit<IBaseSearchRequest, "startTime"> & {
   startTime: Date | undefined;
   query: string;
   type: SearchModuleType;

   collectionId?: Guid;
   savedSearchId?: Guid;
   venueId?: Guid;
   venueGroupId?: Guid;
};

export interface SearchModuleProps {
   initialAddress?: string | null
}

export const SearchModule: React.FC<SearchModuleProps> = ({ initialAddress }) => {
   const searchManager = useContext(SearchContext);
   const { enterpriseUser } = useContext(UserContext);

   const [predefinedAddress, setPredefinedAddress] = useState<string | undefined>(undefined);
   const [availableSpaceTypes, setAvailableSpaceTypes] = useState<SpaceType | undefined>(undefined);

   const searchWorkspaceFormMethods = useForm<SearchModuleForm>({
      defaultValues: {
         type: initialAddress ? SearchModuleType.Geo : SearchModuleType.CurrentLocation,
         query: initialAddress || "",
         reservationMethod: ReservationMethod.Hourly
      }
   });

   const resetFormValues = (valuesToSet: Partial<SearchModuleForm>, valuesToKeep?: (keyof SearchModuleForm)[]) => {
      const currentValues = searchWorkspaceFormMethods.getValues();
      for (const [key, _value] of Object.entries(currentValues)) {
         const typedKey = key as keyof SearchModuleForm;

         if (typeof typedKey === "string") {
            if (typedKey in valuesToSet) {
               searchWorkspaceFormMethods.setValue(typedKey, valuesToSet[typedKey], { shouldDirty: true });
            } else if (valuesToKeep && valuesToKeep.indexOf(typedKey) > -1) {
               //nothing to do
            } else {
               searchWorkspaceFormMethods.setValue(typedKey, undefined, { shouldDirty: true });
            }
         }
      }
   }

   useEffect(() => {
      if (searchManager.config.immutableOptions?.immutableOptions) {
         if (searchManager.config.immutableOptions.immutableOptions.some(option => option == ForceableExtendedSearchOptionsProperties.SpaceTypes) == true
            || searchManager.config.immutableOptions.immutableOptions.some(option => option == ForceableExtendedSearchOptionsProperties.ReservationMethod) == true) {
            const spaceTypes = searchManager.config.immutableOptions.spaceTypes;
            const isMonthlyOnly = searchManager.config.immutableOptions.reservationMethod == WorkspaceReservationMethod.Period;
            if (isMonthlyOnly && contains(spaceTypes, SpaceType.Desk)) {
               // for disable toggle on UI
               setAvailableSpaceTypes(spaceTypes ^ SpaceType.Desk);
            } else {
               setAvailableSpaceTypes(spaceTypes);
            }

         }
      }
   }, [searchManager.config]);

   const findWorkspace = (searchFormValues: SearchModuleForm) => {
      if (searchFormValues.collectionId) {
         searchManager.showMemberCollection(searchFormValues.collectionId);
      }
      else {
         searchManager.search(getSearchRequest(searchFormValues));
      }

   }

   const debouncedFindWorkspace = useCallback(debounce(findWorkspace, 100), []);

   const openSearch = () => {
      window.location.href = searchManager.currentSearchLink();
   }

   const onSubmit = () => {
      searchWorkspaceFormMethods.handleSubmit(debouncedFindWorkspace)();
   }

   const onInputSubmit = (value) => {
      resetFormValues({
         query: value,
         type: value ? SearchModuleType.FullText : SearchModuleType.CurrentLocation
      }, ["minCapacity", "spaceTypes", "startTime"]);

      onSubmit();
   }

   const onInputChange = (value) => {
      if (value) {
         searchWorkspaceFormMethods.setValue("type", SearchModuleType.FullText, { shouldDirty: true, shouldValidate: true });
      }
      else {
         searchWorkspaceFormMethods.setValue("type", SearchModuleType.CurrentLocation, { shouldDirty: true, shouldValidate: true });
      }
   }

   const onItemSelected = (selectedOption: KnownSearchAutocomplete) => {
      if (selectedOption) {
         resetFormValues(
            getSearchModuleForm(selectedOption),
            selectedOption instanceof CollectionSearchAutocomplete || selectedOption instanceof SavedSearchRequestAutocomplete
               ? undefined
               : ["minCapacity", "spaceTypes", "startTime"]);

         onSubmit();
      }
   }

   useEffect(() => {
      const subscription = searchWorkspaceFormMethods.watch((value, { name, type }) => {
         if (isAutosubmitable(name)) {
            onSubmit();
         }
         if (name && name == "savedSearchId" && value.savedSearchId) {
            searchManager.getSavedSearchCached(value.savedSearchId, true).then(savedSearch => {
               const mappedForm = getSearchModuleForm(savedSearch.request);
               const predefinedAddress = getPredefinedAddress(savedSearch);
               resetFormValues({
                  type: mappedForm.query ? SearchModuleType.Geo : SearchModuleType.CurrentLocation,
                  query: mappedForm.query ?? predefinedAddress,
                  spaceTypes: mappedForm.spaceTypes,
                  minCapacity: mappedForm.minCapacity,
                  startTime: mappedForm.startTime
               });
               setPredefinedAddress(predefinedAddress);
            }).catch(e => console.error(e));
         }
      });

      return () => subscription.unsubscribe();
   }, [searchWorkspaceFormMethods.watch, searchManager.config]);

   useEffect(() => {
      if (initialAddress) {
         setPredefinedAddress(initialAddress);
         searchManager.search(new FullTextSearchRequest({
            ...new FullTextSearchRequest() as IBaseSearchRequest,
            fullTextQuery: initialAddress
         }))
      }
      else if (enterpriseUser.homeBaseSearchId) {
         searchWorkspaceFormMethods.setValue("savedSearchId", enterpriseUser.homeBaseSearchId);
      } else {
         searchManager.search(new CurrentLocationSearchRequest());
      }

      return function cancel() {
         searchManager.cancelSearch()
      }
   }, []);

   return (<Module>
      <ModuleCaption className="mb-px8">Find your space</ModuleCaption>
      <form className="search-module-form">
         <input type="hidden" {...searchWorkspaceFormMethods.register("venueId")} />
         <input type="hidden" {...searchWorkspaceFormMethods.register("venueGroupId")} />
         <input type="hidden" {...searchWorkspaceFormMethods.register("collectionId")} />
         <input type="hidden" {...searchWorkspaceFormMethods.register("savedSearchId")} />
         <input type="hidden" {...searchWorkspaceFormMethods.register("type")} />
         <div>
            <SearchAutocompleteWithDropdown size="lg" control={searchWorkspaceFormMethods.control} name="query" placeholder="Search by ZIP" onInputSubmit={onInputSubmit} onItemSelected={onItemSelected} onInputChange={onInputChange} inputValue={predefinedAddress} />
         </div>
         <div className="search-module-row">
            <div className="search-module-col-fluid">
               <DatePickerInputControlled bsSize="lg" control={searchWorkspaceFormMethods.control} name="startTime" placeholderText="This Week" minDate={new Date()} elementToAttach={document.body} />
            </div>
            <div className="search-module-capacity">
               <CapacityInputControlled bsSize="lg" debounceMiliseconds={500} control={searchWorkspaceFormMethods.control} name="minCapacity" aria-label="workspace capacity" />
            </div>
         </div>
         <div className="search-module-row">
            <div className="search-module-col-fluid">
               <SpaceTypeToggles control={searchWorkspaceFormMethods.control} name="spaceTypes" spaceType={availableSpaceTypes} />
            </div>
            <div className="search-module-cta">
               <SpinningButton isLoading={searchManager.isSearching} size="lg" color="primary" fluid onClick={(e) => { e.preventDefault(); openSearch(); }}>Search</SpinningButton>
            </div>
         </div>
      </form>
   </Module>);
}